import { Box, MenuItem, Typography, styled } from '@mui/material';
import React from 'react';

import ChartCard from 'components/charts/chart-card';
import Select from 'components/select';
import Switch from 'components/switch';
import DataLab from 'views/playground/features/data-lab/components/data-lab';
import { GalleryScreenBody } from 'views/playground/features/data-lab/components/screens/gallery-screen';
import { useChartSettingsState } from 'contexts/chart/chart-settings-context';
import { ChartPaletteOption } from 'components/charts/types/chart-settings.type';
import ChartThemeScope from 'components/charts/theme/chart-theme-scope';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
}));

const PageTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: chartPalette.text.primary,
}));

const Subtitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 14,
  fontWeight: 400,
  color: chartPalette.text.secondary,
}));

const ControlsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(6),
}));

const ControlGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 160,
}));

const PreviewHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ theme, chartPalette }) => ({
  fontSize: 18,
  fontWeight: 700,
  color: chartPalette.text.primary,
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(4),
}));

// Palette and boolean settings use the shared input wrappers. Toggle groups
// provide controlled mode and density choices.
export const ControlsScreenBody: React.FC = () => {
  const chartPalette = useChartPalette();
  const { settings, setMode, setPalette, setAnimationsEnabled, setGridlinesEnabled, setLegendsEnabled, setDensity } =
    useChartSettingsState();

  return (
    <>
      <HeaderRow>
        <PageTitle chartPalette={chartPalette} variant="h2">
          Controls
        </PageTitle>
        <Subtitle chartPalette={chartPalette}>Every control below changes the Gallery rendered beneath it</Subtitle>
      </HeaderRow>

      <ChartThemeScope>
        <ChartCard title="Display settings" height={0}>
          <ControlsRow>
            <ControlGroup>
              <Select
                label="Appearance"
                value={settings.mode}
                dark={settings.mode === 'light'}
                onChange={(event) => {
                  const mode = event.target.value;
                  if (mode === 'light' || mode === 'dark') setMode(mode);
                }}>
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </Select>
            </ControlGroup>

            <ControlGroup>
              <Select
                label="Palette"
                value={settings.palette}
                dark={settings.mode === 'light'}
                onChange={(event) => setPalette(event.target.value as ChartPaletteOption)}>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="legacy">Legacy</MenuItem>
                <MenuItem value="colorBlindSafe">Colorblind-safe</MenuItem>
              </Select>
            </ControlGroup>

            <ControlGroup>
              <Switch
                label="Animations"
                value={settings.animationsEnabled}
                dark={settings.mode === 'light'}
                onChange={(_event, checked) => setAnimationsEnabled(checked)}
              />
            </ControlGroup>

            <ControlGroup>
              <Switch
                label="Gridlines"
                value={settings.gridlinesEnabled}
                dark={settings.mode === 'light'}
                onChange={(_event, checked) => setGridlinesEnabled(checked)}
              />
            </ControlGroup>

            <ControlGroup>
              <Switch
                label="Show legends"
                value={settings.legendsEnabled}
                dark={settings.mode === 'light'}
                onChange={(_event, checked) => setLegendsEnabled(checked)}
              />
            </ControlGroup>
            <ControlGroup>
              <Select
                label="Density"
                value={settings.density}
                dark={settings.mode === 'light'}
                onChange={(event) => {
                  const density = event.target.value;
                  if (density === 'comfortable' || density === 'compact') setDensity(density);
                }}>
                <MenuItem value="comfortable">Comfortable</MenuItem>
                <MenuItem value="compact">Compact</MenuItem>
              </Select>
            </ControlGroup>
          </ControlsRow>
        </ChartCard>
      </ChartThemeScope>

      <PreviewHeading chartPalette={chartPalette} variant="h3">
        Live preview
      </PreviewHeading>
      <GalleryScreenBody />
    </>
  );
};

const ControlsScreen: React.FC = () => (
  <DataLab>
    <ControlsScreenBody />
  </DataLab>
);

export default ControlsScreen;
